import { generateVideo, batchGetVideoStatus } from '../utils/api';

// 视频任务状态枚举
export enum VideoTaskStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed'
}

// 视频任务接口
export interface VideoTask {
  taskId: string;
  sceneId: string;
  status: VideoTaskStatus;
  startTime: number;
  lastPollTime: number;
  retryCount: number;
  videoUrl?: string;
  duration?: number; // 视频时长（从生成参数获取）
  error?: string;
}

// 视频生成参数接口
export interface GenerateVideoParams {
  prompt: string;
  negative_prompt?: string;
  image_urls: string[];
  image_roles: string[];
  duration: number;
  aspect_ratio: string;
  resolution: string;
  fps: number;
  camera_movement?: string;
  style?: string;
  watermark?: boolean;
}

// VideoService类，单例模式实现
class VideoService {
  private static instance: VideoService;
  private tasks: Map<string, VideoTask> = new Map();
  private pollingTimer: NodeJS.Timeout | null = null;
  private callbacks: Set<(task: VideoTask) => void> = new Set();
  private readonly POLLING_INTERVAL = 5000; // 5秒轮询一次
  private readonly MAX_RETRY_COUNT = 30; // 最大重试次数

  // 私有构造函数，防止外部实例化
  private constructor() { }

  // 获取单例实例
  public static getInstance(): VideoService {
    if (!VideoService.instance) {
      VideoService.instance = new VideoService();
    }
    return VideoService.instance;
  }

  // 生成视频
  public async generateVideo(sceneId: string, params: GenerateVideoParams): Promise<string> {
    try {
      const response = await generateVideo(params);
      const taskId = response.data.task_id;

      // 创建新任务
      const newTask: VideoTask = {
        taskId,
        sceneId,
        status: VideoTaskStatus.PROCESSING,
        startTime: Date.now(),
        lastPollTime: Date.now(),
        retryCount: 0,
        duration: params.duration // 保存视频时长参数
      };

      // 保存任务
      this.tasks.set(taskId, newTask);

      // 启动轮询
      this.startPolling();

      return taskId;
    } catch (error) {
      console.error('生成视频失败:', error);
      throw error;
    }
  }

  // 批量查询任务状态
  private async batchPollTaskStatus(): Promise<void> {
    // 获取所有处理中的任务
    const processingTasks = Array.from(this.tasks.values()).filter(task =>
      task.status === VideoTaskStatus.PENDING ||
      task.status === VideoTaskStatus.PROCESSING
    );

    if (processingTasks.length === 0) {
      // 没有处理中的任务，停止轮询
      this.stopPolling();
      return;
    }

    try {
      const taskIds = processingTasks.map(task => task.taskId);
      const response = await batchGetVideoStatus(taskIds);

      // 更新任务状态
      response.data.forEach(taskStatus => {
        const task = this.tasks.get(taskStatus.task_id);
        if (task) {
          // 更新任务状态，确保状态值有效
          const newStatus = taskStatus.status;
          if (Object.values(VideoTaskStatus).includes(newStatus as VideoTaskStatus)) {
            // 确保生成中的状态不被中间状态改变，只有当获取到最终结果时才更新
            const finalStatuses = [VideoTaskStatus.SUCCEEDED, VideoTaskStatus.FAILED];
            // 如果当前任务已经在处理中，且新状态不是最终状态，则保持当前状态
            // 另外，如果UI已经显示生成中（processing），第一次batch调用返回的pending状态不应该覆盖它
            if (!(task.status === VideoTaskStatus.PROCESSING && !finalStatuses.includes(newStatus as VideoTaskStatus))) {
              task.status = newStatus as VideoTaskStatus;
            }
          } else {
            // 如果返回的状态无效，保持当前状态
            console.warn(`收到无效的任务状态: ${newStatus}，保持当前状态 ${task.status}`);
          }
          task.lastPollTime = Date.now();

          if (taskStatus.video_url) {
            task.videoUrl = taskStatus.video_url;
          }

          if (taskStatus.error) {
            task.error = taskStatus.error;
          }

          // 通知所有注册的回调
          this.callbacks.forEach(callback => callback(task));

          // 如果任务失败，增加重试次数
          if (task.status === VideoTaskStatus.FAILED) {
            task.retryCount++;

            // 如果重试次数超过最大值，移除任务
            if (task.retryCount > this.MAX_RETRY_COUNT) {
              this.tasks.delete(task.taskId);
            }
          }

          // 如果任务成功，移除任务
          if (task.status === VideoTaskStatus.SUCCEEDED) {
            // 任务成功，不需要再轮询
            // 保留任务记录，方便后续查询
          }
        }
      });

    } catch (error) {
      console.error('批量查询任务状态失败:', error);
    }
  }

  // 启动轮询
  private startPolling(): void {
    if (!this.pollingTimer) {
      this.pollingTimer = setInterval(() => {
        this.batchPollTaskStatus();
      }, this.POLLING_INTERVAL);
    }
  }

  // 停止轮询
  private stopPolling(): void {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  // 删除视频
  public deleteVideo(sceneId: string): void {
    // 查找该场景的所有任务
    Array.from(this.tasks.values()).forEach(task => {
      if (task.sceneId === sceneId) {
        this.tasks.delete(task.taskId);
      }
    });
  }

  // 获取场景的视频任务
  public getTaskBySceneId(sceneId: string): VideoTask | undefined {
    return Array.from(this.tasks.values()).find(task => task.sceneId === sceneId);
  }

  // 获取所有任务
  public getAllTasks(): VideoTask[] {
    return Array.from(this.tasks.values());
  }

  // 注册任务状态变化回调
  public onTaskStatusChange(callback: (task: VideoTask) => void): void {
    this.callbacks.add(callback);
  }

  // 取消注册回调
  public offTaskStatusChange(callback: (task: VideoTask) => void): void {
    this.callbacks.delete(callback);
  }

  // 清除所有任务
  public clearAllTasks(): void {
    this.tasks.clear();
    this.stopPolling();
  }
}

// 导出单例实例
export const videoService = VideoService.getInstance();
